import { useState, useRef } from 'react';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { ModalShell } from '@/shared/ui/ModalShell';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { parseCsv } from '@/shared/lib/csv';

/**
 * Generic bulk CSV upload modal.
 *
 * Props:
 *   title          — modal title, e.g. "Bulk Upload Users"
 *   subtitle       — modal subtitle
 *   entityLabel    — singular noun for the upload button, e.g. "User" → "Upload 3 Users"
 *   requiredColumns — string[] — columns that must exist in the CSV
 *   filterRow      — (row) => bool — keep row in preview (default: always true)
 *   previewColumns  — [{ key, label, flex? }] — columns to show in preview table
 *   sampleCsv      — string — raw CSV content for the sample download
 *   sampleFilename — filename for the sample download
 *   mutateAsync    — async (file) => { success, failed, csvUrl? }
 *   onClose        — () => void
 */
export function BulkUploadModal({
  title,
  subtitle,
  entityLabel,
  requiredColumns,
  filterRow,
  previewColumns,
  sampleCsv,
  sampleFilename,
  mutateAsync,
  isPending,
  onClose,
}) {
  const c = useThemeColors();
  const [preview, setPreview] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const gridCols = previewColumns.map((col) => col.flex ?? '1fr').join(' ');

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are supported.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('File exceeds 50 MB limit.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const { headers, rows: allRows } = parseCsv(text);
        if (allRows.length === 0) { setError('File is empty or has no data rows.'); return; }
        const missing = requiredColumns.filter((col) => !headers.includes(col));
        if (missing.length) {
          setError(`Missing columns: ${missing.join(', ')}. Download the sample file.`);
          return;
        }
        const rows = allRows.filter(filterRow ?? (() => true));
        setSelectedFile(file);
        setTotalRows(rows.length);
        setPreview(rows.slice(0, 10));
      } catch {
        setError('Failed to parse file. Ensure it is a valid .csv file.');
      }
    };
    reader.readAsText(file);
  }

  async function handleConfirm() {
    try {
      const res = await mutateAsync(selectedFile);
      const success = res?.success ?? 0;
      const failed = res?.failed ?? 0;
      const csvUrl = res?.csvUrl ?? res?.reportUrl ?? null;
      setResult({ success, failed, csvUrl });
    } catch (err) {
      // Axios errors are normalized to { status, code, message } in shared/api/axios.js,
      // so the BE message lives at err.message (not err.response.data.message) (AUT-147).
      const msg = err?.message ?? err?.response?.data?.message ?? 'Upload failed. Please try again.';
      setError(msg);
    }
  }

  function handleDownloadSample() {
    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = sampleFilename ?? 'sample.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  function handleDownloadReport(csvUrl) {
    const a = document.createElement('a');
    a.href = csvUrl; a.download = 'upload-report.csv'; a.click();
  }

  function resetFile() {
    setPreview(null);
    setSelectedFile(null);
    setTotalRows(0);
    setError('');
  }

  // ── Result screen ────────────────────────────────────────────────────────
  if (result) {
    const allOk = result.failed === 0;
    return (
      <ModalShell
        onClose={onClose}
        icon={allOk ? 'check_circle' : 'error'}
        iconVariant={allOk ? 'primary' : 'warning'}
        title="Upload Complete"
        subtitle={`${result.success} success · ${result.failed} failed`}
        maxWidth="420px"
        footer={
          <>
            {result.csvUrl && (
              <PrimaryButton variant="secondary" icon="download" onClick={() => handleDownloadReport(result.csvUrl)}>
                Download Report
              </PrimaryButton>
            )}
            <PrimaryButton onClick={onClose}>Done</PrimaryButton>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '16px', borderRadius: radius.lg, background: semantic.successGreenBg, border: `1px solid ${semantic.successGreenBorder}` }}>
              <p style={{ margin: '0 0 4px', fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: semantic.successGreen }}>{result.success}</p>
              <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: c.textMuted, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.semibold }}>Success</p>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: '16px', borderRadius: radius.lg, background: result.failed > 0 ? 'rgba(239,68,68,0.08)' : c.bgSubtle, border: `1px solid ${result.failed > 0 ? 'rgba(239,68,68,0.2)' : c.borderSubtle}` }}>
              <p style={{ margin: '0 0 4px', fontSize: typography.fontSize['5xl'], fontWeight: typography.fontWeight.bold, color: result.failed > 0 ? semantic.error : c.textMuted }}>{result.failed}</p>
              <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: c.textMuted, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.semibold }}>Failed</p>
            </div>
          </div>
          {result.csvUrl && (
            <p style={{ margin: 0, fontSize: typography.fontSize.base, color: c.textMuted }}>
              Download the report to see details on failed rows.
            </p>
          )}
        </div>
      </ModalShell>
    );
  }

  // ── Upload / Preview screen ──────────────────────────────────────────────
  return (
    <ModalShell
      onClose={onClose}
      icon="upload_file"
      iconVariant="primary"
      title={title}
      subtitle={subtitle}
      maxWidth="580px"
      footer={
        !preview ? (
          <PrimaryButton variant="secondary" onClick={onClose}>Cancel</PrimaryButton>
        ) : (
          <>
            <PrimaryButton variant="secondary" onClick={onClose} disabled={isPending}>Cancel</PrimaryButton>
            <PrimaryButton icon="upload" onClick={handleConfirm} disabled={isPending}>
              {isPending ? 'Uploading…' : `Upload ${totalRows} ${entityLabel}${totalRows !== 1 ? 's' : ''}`}
            </PrimaryButton>
          </>
        )
      }
    >
      {!preview ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${c.borderSubtle}`, borderRadius: radius.xl, padding: '40px 20px', textAlign: 'center', cursor: 'pointer', background: c.bgSubtle }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 36, color: c.textSubtle, display: 'block', marginBottom: '8px' }}>upload_file</span>
            <p style={{ margin: '0 0 4px', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textLabel }}>Click to select .csv file</p>
            <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: c.textSubtle }}>Required columns: {requiredColumns.join(', ')}</p>
            <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
          </div>
          {error && <p style={{ margin: 0, fontSize: typography.fontSize.base, color: semantic.error, fontWeight: typography.fontWeight.medium, whiteSpace: 'pre-wrap' }}>{error}</p>}
          <button
            onClick={handleDownloadSample}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: typography.fontSize.base, color: c.textActive, display: 'inline-flex', alignItems: 'center', gap: '4px', fontFamily: typography.fontFamily.body, padding: 0 }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>download</span>
            Download sample file
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <p style={{ margin: 0, fontSize: typography.fontSize.md, color: c.textLabel }}>Preview: first {preview.length} of {totalRows} rows</p>
          <div style={{ border: `1px solid ${c.borderSubtle}`, borderRadius: radius.lg, overflow: 'hidden', fontSize: typography.fontSize.base }}>
            <div style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '8px 14px', background: c.bgMuted, borderBottom: `1px solid ${c.borderSubtle}` }}>
              {previewColumns.map((col) => (
                <span key={col.key} style={{ fontWeight: typography.fontWeight.bold, color: c.textMuted, textTransform: 'uppercase', fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.label }}>{col.label}</span>
              ))}
            </div>
            {preview.map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '8px 14px', borderBottom: i < preview.length - 1 ? `1px solid ${c.divider}` : 'none' }}>
                {previewColumns.map((col, ci) => (
                  <span key={col.key} style={{ color: ci === 0 ? c.textPrimary : c.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row[col.key]}</span>
                ))}
              </div>
            ))}
          </div>
          {error && <p style={{ margin: 0, fontSize: typography.fontSize.base, color: semantic.error, fontWeight: typography.fontWeight.medium, whiteSpace: 'pre-wrap' }}>{error}</p>}
          <button
            onClick={resetFile}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: typography.fontSize.base, color: c.textSubtle, fontFamily: typography.fontFamily.body, alignSelf: 'flex-start', padding: 0 }}
          >
            ← Choose different file
          </button>
        </div>
      )}
    </ModalShell>
  );
}
